import {
  Box,
  Button,
  Dialog,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import LanguageTabs from "../LanguageTabs";
import {
  LOCALISED_ADVISORY_LENGTHS,
  LOCALISED_STORAGE_LIMITS,
  LOCALISED_TEXT_FIELDS,
  emptyLocalisedRow,
  localisedFieldLength,
  localisedStatus,
  type LocalisedTextField,
} from "@shared/vod-localised";
import { canonicalAssetLanguage } from "@shared/vod-title";
import type { LocalizedAssetInfo } from "@shared/types/content-api";
import { tokens } from "../../theme";

/**
 * "Localised content" - the panel that edits `localizedInfo[]` (`BE-8`,
 * re-pointed by `BE-15`).
 *
 * A side panel rather than a nested modal: the form you came from stays
 * visible, because which title you are editing is context you should not have
 * to remember.
 *
 * ## It edits the WIRE rows
 *
 * `LocalizedAssetInfo[]`, exactly as the catalogue stores them, not a
 * `{[language]: {...}}` map. The map shape is what the mockup carried and it is
 * a paraphrase with a bug in it: upstream the primary key is
 * `(assetId, language)` under a case-insensitive collation, so the array
 * already keys itself. What a map hides is the one hazard that is real - a
 * payload naming the same language twice, which is a bare 500 on create and a
 * silent last-wins on update.
 *
 * ## Two limits, and only one of them refuses
 *
 * - **Storage** (`LOCALISED_STORAGE_LIMITS`): `VARCHAR(255)` title, and
 *   `TEXT` = **65 535 BYTES** per synopsis under `STRICT_TRANS_TABLES`. Facts
 *   off the schema, enforced once by the server where the value is really
 *   written. The counter turns red at these and the save will be refused.
 * - **Advice** (`LOCALISED_ADVISORY_LENGTHS`): how much text the TV templates
 *   were drawn to hold. The counter turns amber and **nothing else happens**.
 *
 * ⚠️ Nothing here truncates. This panel used to `slice()` on every keystroke;
 * text arriving from the catalogue is routinely longer than a template was
 * drawn for, and dropping its tail in an editor is data loss with a green tick
 * on it.
 *
 * ## A blank language is accepted, and reported
 *
 * The legacy portal creates a row for every tab somebody clicks, and the device
 * transformer delivers every row it finds - so blank rows exist in quantity and
 * a rule that refused them would make an imported title unsavable. Removing the
 * language is how you delete one, and `localisedIssues` is what says it is
 * there.
 */

const ROWS: Record<LocalisedTextField, number> = {
  title: 1,
  synopsisShort: 3,
  synopsisMedium: 5,
  synopsisLong: 8,
};

export default function LocalisedContentPanel({
  open,
  rows,
  onClose,
  onSave,
}: {
  open: boolean;
  rows: LocalizedAssetInfo[];
  onClose: () => void;
  onSave: (next: LocalizedAssetInfo[]) => void;
}) {
  const [draft, setDraft] = useState<LocalizedAssetInfo[]>(rows);
  const [language, setLanguage] = useState(rows[0]?.language ?? "sv");

  // Reseeded on each open: this panel is mounted by a dialog that stays
  // mounted, so without it the second open would show the first open's edits.
  // (The dialog above it is remounted by key instead - it can be, because its
  // caller knows which record changed. This one cannot see that.)
  const [seeded, setSeeded] = useState(false);
  if (open && !seeded) {
    setDraft(rows);
    setLanguage(rows[0]?.language ?? "sv");
    setSeeded(true);
  }
  if (!open && seeded) setSeeded(false);

  const languages = draft.map((row) => row.language);
  const entry =
    draft.find((row) => row.language === language) ??
    emptyLocalisedRow(language);

  const statuses = Object.fromEntries(
    draft.map((row) => [row.language, localisedStatus(row)])
  );

  function setField(field: LocalisedTextField, value: string) {
    setDraft((prev) => {
      const index = prev.findIndex((row) => row.language === language);
      const next = [...prev];
      if (index === -1) {
        next.push({ ...emptyLocalisedRow(language), [field]: value });
      } else {
        next[index] = { ...next[index], [field]: value };
      }
      return next;
    });
  }

  function addLanguage(code: string) {
    const canonical = canonicalAssetLanguage(code);
    if (!canonical) return;
    setDraft((prev) =>
      prev.some((row) => row.language === canonical)
        ? prev
        : [...prev, emptyLocalisedRow(canonical)]
    );
    setLanguage(canonical);
  }

  function removeLanguage(code: string) {
    setDraft((prev) => {
      const next = prev.filter((row) => row.language !== code);
      if (code === language) setLanguage(next[0]?.language ?? "sv");
      return next;
    });
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 440,
          maxWidth: "100%",
          m: 0,
          ml: "auto",
          mr: 2,
          height: "calc(100% - 32px)",
          maxHeight: "none",
        },
      }}
      data-testid="manage-localised-content"
    >
      <Stack sx={{ p: 3, height: "100%" }}>
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{ fontSize: 20, fontWeight: 600, color: tokens.ink, mb: 0.5 }}
          >
            Localised content
          </Typography>
          <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
            Titles and descriptions per language. Removing a language deletes
            its row when you save.
          </Typography>
        </Box>

        <LanguageTabs
          languages={languages}
          value={language}
          statuses={statuses}
          onChange={setLanguage}
          onAdd={addLanguage}
        />

        {languages.length === 0 ? (
          <Typography
            sx={{ fontSize: 13, color: tokens.inkMuted, flex: "1 1 auto" }}
          >
            No languages yet. Add one to write a title and its descriptions.
          </Typography>
        ) : (
          <Stack spacing={2.5} sx={{ flex: "1 1 auto", overflowY: "auto" }}>
            {LOCALISED_TEXT_FIELDS.map((f) => {
              const value = entry[f.key] ?? "";
              const advisory = LOCALISED_ADVISORY_LENGTHS[f.key];
              const storage = LOCALISED_STORAGE_LIMITS[f.key];
              const measured = localisedFieldLength(f.key, value);
              const overStorage = measured.length > storage;
              const overAdvisory = value.length > advisory;
              return (
                <Box key={f.key}>
                  <Stack
                    direction="row"
                    alignItems="baseline"
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                  >
                    <Typography
                      sx={{ fontSize: 12, fontWeight: 700, color: tokens.ink }}
                    >
                      {f.label}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: tokens.inkMuted }}>
                      Fits {advisory} characters
                    </Typography>
                  </Stack>
                  <TextField
                    fullWidth
                    multiline={ROWS[f.key] > 1}
                    rows={ROWS[f.key] > 1 ? ROWS[f.key] : undefined}
                    value={value}
                    onChange={(e) => setField(f.key, e.target.value)}
                    error={overStorage}
                    data-testid={`localised-${f.key}`}
                  />
                  <Typography
                    sx={{
                      fontSize: 11,
                      mt: 0.5,
                      color: overStorage
                        ? tokens.error
                        : overAdvisory
                          ? tokens.warning
                          : tokens.inkMuted,
                    }}
                  >
                    {measured.length} {measured.unit}
                    {overStorage
                      ? ` — over the column's ${storage} ${measured.unit}. The catalogue will refuse this.`
                      : overAdvisory
                        ? ` — longer than the TV layout was drawn for. It is still saved in full.`
                        : ""}
                  </Typography>
                </Box>
              );
            })}

            <Button
              size="small"
              onClick={() => removeLanguage(language)}
              sx={{ color: tokens.error, alignSelf: "flex-start" }}
              data-testid="localised-remove-language"
            >
              Remove {language.toUpperCase()}
            </Button>
          </Stack>
        )}

        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{ pt: 3, flex: "0 0 auto" }}
        >
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => onSave(draft)}
            data-testid="localised-save"
          >
            Save languages
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
}
